import {
  Channel,
  MessageInput,
  MessageList,
  useChannelActionContext,
  Window,
} from "stream-chat-react";
import CustomChannelHeader from "./channel-header";
import ChatList from "./chat-list";
import { useEffect, useState } from "react";
import {
  ChannelSearchTextIcon,
  GroupLeaveIcon,
  GroupSettingIcon,
  GroupUserIcon,
} from "../../../icons/group-chat-icons";
import { Cross } from "../../../icons";
import { DownButtonIcon } from "../../../icons/academy-icons";
import { useDispatch } from "react-redux";
import { useAppSelector } from "../../../hooks/store-hooks";
import { getAllChattedChannel } from "../../../store/actions/chatActions";
import { LeftArrowIcon } from "../../../icons/tags-icons";
import Button from "../../common/button";

const LeftPanel = ({
  chatClient = () => null,
  channel,
  onSelectUser,
  handleLeftPanelWithList,
  hideLeftPanel,
  selectedUserData,
  handelJoinChannel,
  joinChannelIsLoading,
}: any) => {
  const [groupInfoVisible, setGroupInfoVisible] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  const JumpToMessage = () => {
    const { jumpToMessage } = useChannelActionContext();
    const [searchText, setSearchText] = useState("");
    const [results, setResults] = useState<any[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [page, setPage] = useState(0);
    const limit = 100,
      debounceDelay = 1000;
    const [disableNext, setDisableNext] = useState(false);

    useEffect(() => {
      const debounceTimer = setTimeout(() => {
        if (searchText) {
          handleSearch(true);
        }
      }, debounceDelay);

      return () => clearTimeout(debounceTimer);
    }, [searchText]);

    const handleSearch = async (newSearch = true) => {
      const response = await chatClient.search(
        {
          cid: channel.cid,
        },
        { text: { $autocomplete: searchText } },
        {
          sort: [{ relevance: -1 }, { updated_at: -1 }],
          limit,
          offset: newSearch ? 0 : results.length,
        },
      );

      if (newSearch) {
        setResults(response.results);
        setPage(1);
        setCurrentIndex(0);
      } else {
        setResults((prevResults) => [...prevResults, ...response.results]);
        setPage(page + 1);
      }
      if (response.results.length && newSearch) {
        jumpToMessage(response.results[0].message.id);
      }
    };

    useEffect(() => {
      if (results.length > 0 && currentIndex < results.length) {
        jumpToMessage(results[currentIndex].message.id);
      }
    }, [results, currentIndex, jumpToMessage]);

    const jumpToNextMessage = async () => {
      if (currentIndex < results.length) {
        const nextIndex = currentIndex + 1;
        setCurrentIndex(nextIndex);
      } else if (results.length === limit * page) {
        await handleSearch(false);
        const nextIndex = currentIndex + 1;
        setCurrentIndex(nextIndex);
      } else if (
        results.length < limit * page &&
        currentIndex < results.length + 1
      ) {
        const nextIndex = currentIndex + 1;
        setCurrentIndex(nextIndex);
        setDisableNext(true);
      }
    };

    const jumpToPreviousMessage = () => {
      if (currentIndex > 0) {
        const prevIndex = currentIndex - 1;
        setCurrentIndex(prevIndex);
      }
    };

    return (
      <div className="absolute h-full left-0 w-full bg-gray-100 px-4 py-3">
        <div className="bg-[#EBEBEB] rounded-lg h-full flex items-center px-4 justify-between">
          <div className="flex items-center gap-2">
            <button
              data-testid="jump-to-message"
              onClick={() => setShowSearch(false)}
            >
              <LeftArrowIcon />
            </button>
            <input
              data-testid="search-text"
              onChange={(e) => setSearchText(e.target.value)}
              value={searchText}
              className="bg-transparent border-none focus:outline-none w-80"
            />
          </div>
          <div className="flex items-center gap-2">
            <button onClick={jumpToNextMessage} disabled={disableNext}>
              <DownButtonIcon width={18} height={18} rotate={180} />
            </button>
            <button
              onClick={jumpToPreviousMessage}
              disabled={currentIndex === 0}
            >
              <DownButtonIcon width={18} height={18} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const { homeData } = useAppSelector((state: any) => state.home ?? {});
  const user = homeData?.user ?? {};
  const userRole = channel?.state?.membership?.channel_role;
  const channelType = channel?.type;
  const dispatch = useDispatch<any>();

  const handleLeaveGroup = async () => {
    await channel.removeMembers([user?._id]);
    handleLeftPanelWithList();
    dispatch(
      getAllChattedChannel({ chatClient, userChatId: user?._id, type: "team" }),
    );
    setGroupInfoVisible(false);
  };
  return (
    <div className="left-panel h-full shadow border border-neutral-100">
      {channel ? (
        <Channel channel={channel} TypingIndicator={() => null}>
          <Window>
            <CustomChannelHeader
              handleLeftPanelWithList={handleLeftPanelWithList}
              selectedUserData={selectedUserData}
              setGroupInfoVisible={setGroupInfoVisible}
              handleLeaveGroup={handleLeaveGroup}
              setShowSearch={setShowSearch}
              showSearch={showSearch}
              JumpToMessage={JumpToMessage}
              hideLeftPanel={hideLeftPanel}
            />
            <MessageList />
            {selectedUserData?.is_joined === false ? (
              <Button
                customClasses="text-white text-lg font-semibold font-['SF Pro'] leading-normal w-full px-4 py-2 bg-[#007aff] rounded-[44px]"
                onClick={() => {
                  handelJoinChannel(channel.id, channel.type);
                }}
                isSubmitting={joinChannelIsLoading}
              >
                Join
              </Button>
            ) : channelType === "adminOnly" &&
              userRole !== "channel_moderator" ? (
              <div className="justify-center items-center inline-flex px-4 mb-3">
                <p className="text-black font-semibold">
                  Only admins can send message
                </p>
              </div>
            ) : (
              <MessageInput grow={true} />
            )}
          </Window>
        </Channel>
      ) : (
        <ChatList onSelectUser={onSelectUser} hideLeftPanel={hideLeftPanel} />
      )}
      {selectedUserData?.type === "team" && (
        <GroupInfo
          selectedUserData={selectedUserData}
          groupInfoVisible={groupInfoVisible}
          setGroupInfoVisible={setGroupInfoVisible}
          members={channel?.state?.members}
          groupData={selectedUserData}
          handleLeaveGroup={handleLeaveGroup}
          setShowSearch={setShowSearch}
        />
      )}
    </div>
  );
};

const GroupInfo = ({
  groupInfoVisible,
  setGroupInfoVisible,
  members,
  groupData,
  handleLeaveGroup,
  setShowSearch,
  selectedUserData,
}: {
  groupInfoVisible: boolean;
  setGroupInfoVisible: any;
  members: any;
  groupData: any;
  handleLeaveGroup: any;
  setShowSearch: any;
  selectedUserData: any;
}) => {
  const [viewPanel, setViewPanel] = useState("info");
  if (!groupInfoVisible) return null;
  return (
    <div className="absolute flex top-0 w-full h-full left-0 bg-gray-400">
      {/* Left Panel - Tab Selection */}
      <div className="w-1/4 bg-white h-full p-2 py-4">
        <h1 className="text-xl font-semibold mb-8 px-2">Group</h1>
        <div className="flex flex-col gap-2 text-sm">
          <button
            className={`text-left gap-2 p-2 py-1 flex rounded-md ${
              viewPanel === "info" ? "bg-[#F2F2F7]" : ""
            }`}
            onClick={() => setViewPanel("info")}
          >
            <GroupSettingIcon /> <p>Info</p>
          </button>
          <button
            className={`text-left gap-2 p-2 py-1 flex rounded-md ${
              viewPanel === "members" ? "bg-[#F2F2F7]" : ""
            }`}
            onClick={() => setViewPanel("members")}
          >
            <GroupUserIcon /> <p>Members</p>
          </button>
        </div>
      </div>

      {/* Right Panel - Content based on selected tab */}
      <div className="w-3/4 bg-[#F5F5F5] h-full p-4">
        {viewPanel === "info" && (
          <div className="flex flex-col items-center gap-4">
            <div className="flex justify-between items-center w-full">
              <h2 className="text-2xl font-bold mb-2">Info</h2>
              <div
                className="cursor-pointer"
                onClick={() => setGroupInfoVisible(false)}
              >
                <Cross />
              </div>
            </div>
            <div className="w-40 h-40 bg-gray-200 rounded-full m-auto relative overflow-hidden">
              <img
                src={groupData?.user?.image}
                className="inset-0 absolute object-cover w-full h-full"
              />
            </div>
            <h1 className="text-4xl font-semibold">{groupData?.user?.name}</h1>
            <h3 className="text-xl text-[#404040]">
              {groupData?.user?.member_count} members
            </h3>
            <ul className="p-2 w-full flex flex-col gap-4 font-semibold">
              <li className="bg-white p-4 rounded-lg">
                Become Financially Free
              </li>
              {selectedUserData?.is_joined !== false ? (
                <>
                  <li className="bg-white flex justify-between items-center p-4 rounded-lg cursor-pointer">
                    <div
                      className="flex items-center gap-2"
                      onClick={() => {
                        setShowSearch(true);
                        setGroupInfoVisible(false);
                      }}
                    >
                      <ChannelSearchTextIcon /> <p>Search Chat</p>
                    </div>{" "}
                    <DownButtonIcon width={18} height={18} rotate={270} />{" "}
                  </li>
                  <li
                    className="bg-white flex p-4 rounded-lg cursor-pointer"
                    onClick={handleLeaveGroup}
                  >
                    <GroupLeaveIcon />{" "}
                    <p className="text-[#FF271B]">Leave group</p>{" "}
                  </li>
                </>
              ) : null}
            </ul>
          </div>
        )}
        {viewPanel === "members" && (
          <div className="flex flex-col items-center p-2 h-full">
            <div className="flex justify-between items-center w-full">
              <h2 className="text-2xl font-bold mb-2">Members</h2>
              <div
                className="cursor-pointer"
                onClick={() => setGroupInfoVisible(false)}
              >
                <Cross />
              </div>
            </div>
            <ul className="mt-4 border-t-[1px] border-x-[1px] rounded-lg bg-white h-[90%] overflow-y-auto w-full">
              {members &&
                Object.values(members).map((member: any) => (
                  <li
                    key={member.user.id}
                    className="p-4 border-b-[1px] w-full"
                  >
                    <UserItem user={member.user} />
                  </li>
                ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

const UserItem = ({ user }: any) => {
  return (
    <div className="flex items-center gap-2">
      <div className="w-8 h-8 relative rounded-full overflow-hidden">
        <img
          className="absolute w-full h-full inset-0 object-cover"
          src={user.image}
          alt={user.name}
        />
      </div>
      <p className="font-semibold">{user.name}</p>
    </div>
  );
};

export default LeftPanel;
